import { produce } from "immer";

import fetchDb from "../utils/customRequestFunction";
import { getItem } from "../utils/getFunction";

import { AdminSliceCreator, AdminStoreState } from "./useAdminStore";

export interface DependencyStoreState {
   fetchItemDependencies: (
      productId: number,
      stageId: number,
      optionId: number,
      itemId: number
   ) => Promise<void>;

   addItemDependency: (
      productId: number,
      stageId: number,
      optionId: number,
      itemId: number,
      affectingItemId: number
   ) => Promise<void>;
   removeItemDependency: (
      productId: number,
      stageId: number,
      optionId: number,
      itemId: number,
      dependencyId: number
   ) => Promise<void>;

   addPriceDependency: (
      productId: number,
      stageId: number,
      optionId: number,
      itemId: number,
      affectingItemId: number,
      priceAdjustment: number
   ) => Promise<void>;
   removePriceDependency: (
      productId: number,
      stageId: number,
      optionId: number,
      itemId: number,
      dependencyId: number
   ) => Promise<void>;

   addNameDependency: (
      productId: number,
      stageId: number,
      optionId: number,
      itemId: number,
      affectingItemId: number,
      nameChange: string
   ) => Promise<void>;
   removeNameDependency: (
      productId: number,
      stageId: number,
      optionId: number,
      itemId: number,
      dependencyId: number
   ) => Promise<void>;

   addLearnMoreDependency: (
      productId: number,
      stageId: number,
      optionId: number,
      itemId: number,
      affectingItemId: number,
      learnMoreId: number
   ) => Promise<void>;
   removeLearnMoreDependency: (
      productId: number,
      stageId: number,
      optionId: number,
      itemId: number,
      dependencyId: number
   ) => Promise<void>;

   updateDependencies: (
      productId: number,
      stageId: number,
      optionId: number,
      dependentItemId: number,
      allDeps: {
         dependentItemId: number;
         affectingItemId: number;
         isItemDependency: boolean;
         priceAdjustment: number;
         affectingOptionId: number;
         nameChange?: string;
         learnMoreId?: number;
      }[]
   ) => Promise<void>;
}
export const createDependencySlice: AdminSliceCreator<DependencyStoreState> = (
   set
) => ({
   fetchItemDependencies: async (productId, stageId, optionId, itemId) => {
      console.log("fetching item dependencies");
      try {
         // Expect an array of managers or []
         const managers = await fetchDb.get(`/api/dependencies/${itemId}`);
         console.log("managers", managers);
         set(
            produce((state) => {
               const item = getItem(
                  state.allProducts,
                  productId,
                  stageId,
                  optionId,
                  itemId
               );
               if (!item) return;
               item.dependentDependencyManager = managers || [];
            })
         );
      } catch (error) {
         console.error("Error fetching item dependencies:", error);
      }
   },
   async addItemDependency(
      productId,
      stageId,
      optionId,
      itemId,
      affectingItemId
   ) {
      try {
         const created = await fetchDb.post("/api/dependencies/item", {
            dependentItemId: itemId,
            affectingItemId,
         });

         set(
            produce((state) => {
               const item = getItem(
                  state.allProducts,
                  productId,
                  stageId,
                  optionId,
                  itemId
               );
               if (!item) return;

               if (!item.dependentDependencyManager) {
                  item.dependentDependencyManager = [];
               }

               let manager = item.dependentDependencyManager.find(
                  (m) => m.id === created.dependencyManagerId
               );
               if (!manager) {
                  manager = {
                     id: created.dependencyManagerId,
                     dependentItemId: itemId,
                     affectingItemId,
                     itemDependency: undefined,
                     priceDependency: undefined,
                     nameDependency: undefined,
                     learnMoreDependency: undefined,
                  };
                  item.dependentDependencyManager.push(manager);
               }
               manager.itemDependency = created;
            })
         );
      } catch (error) {
         console.error("Error adding item dependency:", error);
      }
   },

   async removeItemDependency(
      productId,
      stageId,
      optionId,
      itemId,
      dependencyId
   ) {
      try {
         await fetchDb.delete(`/api/dependencies/item/${dependencyId}`);

         set(
            produce((state) => {
               const item = getItem(
                  state.allProducts,
                  productId,
                  stageId,
                  optionId,
                  itemId
               );
               if (!item?.dependentDependencyManager) return;

               item.dependentDependencyManager.forEach((mgr) => {
                  if (
                     mgr.itemDependency &&
                     mgr.itemDependency.id === dependencyId
                  ) {
                     mgr.itemDependency = undefined;
                  }
               });
            })
         );
      } catch (error) {
         console.error("Error removing item dependency:", error);
      }
   },

   async addPriceDependency(
      productId,
      stageId,
      optionId,
      itemId,
      affectingItemId,
      priceAdjustment
   ) {
      try {
         const created = await fetchDb.post("/api/dependencies/price", {
            dependentItemId: itemId,
            affectingItemId,
            priceAdjustment,
         });

         set(
            produce((state) => {
               const item = getItem(
                  state.allProducts,
                  productId,
                  stageId,
                  optionId,
                  itemId
               );
               if (!item) return;

               if (!item.dependentDependencyManager) {
                  item.dependentDependencyManager = [];
               }

               let manager = item.dependentDependencyManager.find(
                  (m) => m.id === created.dependencyManagerId
               );
               if (!manager) {
                  manager = {
                     id: created.dependencyManagerId,
                     dependentItemId: itemId,
                     affectingItemId,
                     itemDependency: undefined,
                     priceDependency: undefined,
                     nameDependency: undefined,
                     learnMoreDependency: undefined,
                  };
                  item.dependentDependencyManager.push(manager);
               }
               manager.priceDependency = created;
            })
         );
      } catch (error) {
         console.error("Error adding price dependency:", error);
      }
   },

   async removePriceDependency(
      productId,
      stageId,
      optionId,
      itemId,
      dependencyId
   ) {
      try {
         await fetchDb.delete(`/api/dependencies/price/${dependencyId}`);

         set(
            produce((state) => {
               const item = getItem(
                  state.allProducts,
                  productId,
                  stageId,
                  optionId,
                  itemId
               );
               if (!item?.dependentDependencyManager) return;

               item.dependentDependencyManager.forEach((mgr) => {
                  if (
                     mgr.priceDependency &&
                     mgr.priceDependency.id === dependencyId
                  ) {
                     mgr.priceDependency = undefined;
                  }
               });
            })
         );
      } catch (error) {
         console.error("Error removing price dependency:", error);
      }
   },

   async addNameDependency(
      productId,
      stageId,
      optionId,
      itemId,
      affectingItemId,
      nameChange
   ) {
      try {
         const created = await fetchDb.post("/api/dependencies/name", {
            dependentItemId: itemId,
            affectingItemId,
            nameChange,
         });

         set(
            produce((state) => {
               const item = getItem(
                  state.allProducts,
                  productId,
                  stageId,
                  optionId,
                  itemId
               );
               if (!item) return;

               if (!item.dependentDependencyManager) {
                  item.dependentDependencyManager = [];
               }

               let manager = item.dependentDependencyManager.find(
                  (m) => m.id === created.dependencyManagerId
               );
               if (!manager) {
                  manager = {
                     id: created.dependencyManagerId,
                     dependentItemId: itemId,
                     affectingItemId,
                     itemDependency: undefined,
                     priceDependency: undefined,
                     nameDependency: undefined,
                     learnMoreDependency: undefined,
                  };
                  item.dependentDependencyManager.push(manager);
               }
               manager.nameDependency = created;
            })
         );
      } catch (error) {
         console.error("Error adding name dependency:", error);
      }
   },

   /**
    * REMOVE a NameDependency.
    */
   async removeNameDependency(
      productId,
      stageId,
      optionId,
      itemId,
      dependencyId
   ) {
      try {
         await fetchDb.delete(`/api/dependencies/name/${dependencyId}`);

         set(
            produce((state: AdminStoreState) => {
               const item = getItem(
                  state.allProducts,
                  productId,
                  stageId,
                  optionId,
                  itemId
               );
               if (!item?.dependentDependencyManager) return;

               item.dependentDependencyManager.forEach((mgr) => {
                  if (
                     mgr.nameDependency &&
                     mgr.nameDependency.id === dependencyId
                  ) {
                     mgr.nameDependency = undefined;
                  }
               });
            })
         );
      } catch (error) {
         console.error("Error removing name dependency:", error);
      }
   },

   /**
    * ADD a LearnMoreDependency
    */
   async addLearnMoreDependency(
      productId,
      stageId,
      optionId,
      itemId,
      affectingItemId,
      learnMoreId
   ) {
      try {
         const created = await fetchDb.post("/api/dependencies/learnMore", {
            dependentItemId: itemId,
            affectingItemId,
            learnMoreId,
            dependentOptionId: optionId,
            affectingOptionId: optionId,
         });

         set(
            produce((state) => {
               const item = getItem(
                  state.allProducts,
                  productId,
                  stageId,
                  optionId,
                  itemId
               );
               if (!item) return;

               if (!item.dependentDependencyManager) {
                  item.dependentDependencyManager = [];
               }

               let manager = item.dependentDependencyManager.find(
                  (m) => m.id === created.dependencyManagerId
               );
               if (!manager) {
                  manager = {
                     id: created.dependencyManagerId,
                     dependentItemId: itemId,
                     affectingItemId,
                     itemDependency: undefined,
                     priceDependency: undefined,
                     nameDependency: undefined,
                     learnMoreDependency: undefined,
                  };
                  item.dependentDependencyManager.push(manager);
               }
               manager.learnMoreDependency = created;
            })
         );
      } catch (error) {
         console.error("Error adding learnMore dependency:", error);
      }
   },

   /**
    * REMOVE a LearnMoreDependency
    */
   async removeLearnMoreDependency(
      productId,
      stageId,
      optionId,
      itemId,
      dependencyId
   ) {
      try {
         await fetchDb.delete(`/api/dependencies/learnMore/${dependencyId}`);

         set(
            produce((state: AdminStoreState) => {
               const item = getItem(
                  state.allProducts,
                  productId,
                  stageId,
                  optionId,
                  itemId
               );
               if (!item?.dependentDependencyManager) return;

               item.dependentDependencyManager.forEach((mgr) => {
                  if (
                     mgr.learnMoreDependency &&
                     mgr.learnMoreDependency.id === dependencyId
                  ) {
                     mgr.learnMoreDependency = undefined;
                  }
               });
            })
         );
      } catch (error) {
         console.error("Error removing learnMore dependency:", error);
      }
   },

   updateDependencies: async (
      productId: number,
      stageId: number,
      optionId: number,
      dependentItemId: number,
      allDeps: {
         dependentItemId: number;
         affectingItemId: number;
         isItemDependency: boolean;
         priceAdjustment: number;
         affectingOptionId: number;
         nameChange?: string;
         learnMoreId?: number;
      }[]
   ) => {
      // 1️⃣ Optimistically update the UI
      const optimisticManagers = allDeps.map((dep) => ({
         affectingOptionId: dep.affectingOptionId,
         id: -1, // Temporary ID for UI
         dependentItemId: dep.dependentItemId,
         affectingItemId: dep.affectingItemId,
         itemDependency: dep.isItemDependency ? { id: -1 } : undefined,
         priceDependency:
            dep.priceAdjustment !== 0
               ? { id: -1, priceAdjustment: dep.priceAdjustment }
               : undefined,
         nameDependency: dep.nameChange
            ? {
                 id: -1,
                 nameChange: dep.nameChange,
                 islandicNameChange: dep.nameChange,
              }
            : undefined,
         learnMoreDependency: dep.learnMoreId
            ? { id: -1, learnMoreId: dep.learnMoreId }
            : undefined,
      }));

      let oldManagers: any[] = [];
      try {
         set(
            produce((state: AdminStoreState) => {
               const localItem = getItem(
                  state.allProducts,
                  productId,
                  stageId,
                  optionId,
                  dependentItemId
               );
               if (!localItem) return;
               oldManagers = [...(localItem.dependentDependencyManager || [])];
               localItem.dependentDependencyManager = optimisticManagers;
            })
         );

         // 2️⃣ Send bulk update request to backend
         await fetchDb.post("/api/dependencies/bulk", {
            managers: allDeps.map((dep) => ({
               dependentItemId: dep.dependentItemId,
               affectingItemId: dep.affectingItemId,
               dependentOptionId: optionId,
               affectingOptionId: dep.affectingOptionId,
               nameChange: dep.nameChange || null,
               itemDep: dep.isItemDependency,
               priceChange:
                  dep.priceAdjustment !== 0 ? dep.priceAdjustment : null,
               learnMoreId: dep.learnMoreId || null,
            })),
         });
      } catch (error) {
         console.error("❌ Error in updateDependencies:", error);

         // ⏪ **Rollback UI state if request fails**
         set(
            produce((state: AdminStoreState) => {
               const localItem = getItem(
                  state.allProducts,
                  productId,
                  stageId,
                  optionId,
                  dependentItemId
               );
               if (!localItem) return;
               localItem.dependentDependencyManager = oldManagers;
            })
         );
      }
   },
});
